/**
 * 분할 보기 — 좌 실시간 / 우 긴호흡 기록(docs/SPLITVIEW_DESIGN.md D2-8).
 *
 * `CaptionFeed` 두 인스턴스를 렌더하는 래퍼다(D2, "내부 2열" 기각).
 * 레이아웃(≥641px 2열 / ≤640px 탭)·공유 히스토리 로더(D2-6·D2-6b)·패널별
 * 스크롤 상태 보존(D7)·aria/포커스 계약(D6)을 여기서 소유한다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from '@cupia/ui/spinner'
import { CaptionFeed, type CaptionFeedProps } from './caption-feed'
import { LIVE_KINDS, LONGFORM_KINDS, type PanelScrollState } from '../lib/split-view'
import { useSharedLoadMore } from '../lib/use-shared-load-more'

const BREAKPOINT_QUERY = '(max-width: 640px)'

export interface SplitFeedProps
  extends Omit<CaptionFeedProps, 'kinds' | 'live' | 'scrollState' | 'emptyTitle' | 'emptyDescription'> {
  /** 좌측 패널 빈 상태(D5-5) — 라우트가 자기 문구를 준다. */
  liveEmptyTitle?: string
  liveEmptyDescription?: string
}

type PanelKey = 'live' | 'longform'

function isMobile(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(BREAKPOINT_QUERY).matches
}

/**
 * 행 정렬(v5)의 모바일 폴백 판정(SPLITVIEW_ROWS_DESIGN D3-4) — 라우트가
 * `rows` 에서 `SplitFeed` 로 내려갈지를 이 훅으로 정한다.
 *
 * 왜 훅인가: 폴백은 **렌더 시점 판정**이라 폭이 돌아오면 행으로 복귀해야
 * 한다(D3-4). 순수 함수 호출이면 리사이즈에 다시 그려지지 않는다. 문턱은
 * `SplitFeed` 와 **같은 상수**(`BREAKPOINT_QUERY`)를 본다 — 두 개가 되면
 * 그 사이 폭에서 아무 것도 안 뜨는 구간이 생긴다.
 */
export function useSplitRowsFallback(): boolean {
  const [mobile, setMobile] = useState(isMobile)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mql = window.matchMedia(BREAKPOINT_QUERY)
    const onChange = (): void => setMobile(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return mobile
}

/** D6-5 — 모드 전환 알림 문구(화면 폭에 따라 다르다). 진입 시 1회 넣는다. */
function announceSplitOn(mobile: boolean): string {
  return mobile
    ? '분할 보기 켜짐 — 실시간·긴호흡 탭으로 전환합니다'
    : '분할 보기 켜짐 — 왼쪽 실시간, 오른쪽 긴호흡'
}

/**
 * 브레이크포인트·탭 전환 **직전에** 떠나는 패널의 현재 위치를 DOM 에서
 * 직접 읽는다(caption-feed.tsx 의 captureAnchor 와 같은 원리).
 *
 * CaptionFeed 의 onSave(언마운트 cleanup, useLayoutEffect)에 기대지 않는
 * 이유: 전환은 종종 "이 인스턴스 언마운트 + 형제 인스턴스 마운트" 가
 * **같은 커밋**에서 일어나고, 새 인스턴스는 자신의 scrollState.initial 을
 * 마운트 시 1회만(useState 초기화) 읽는다. onSave 로 그 값을 갱신해도
 * 이미 마운트가 끝난 새 인스턴스에는 반영할 길이 없다 — 이 함수로 전환을
 * 트리거하는 이벤트 핸들러 안에서 **미리** 읽어 둔다.
 *
 * D7 기준선 보존(codex 지적 #1, fix2 로 갱신) — `unseen`/`seenSeq` 는 예전엔
 * "실측할 방법이 없다" 고 적었지만 그건 틀렸다 — CaptionFeed 가 매 렌더
 * 갱신해 두는 `scrollState.getSnapshot()`(caption-feed.tsx)을 이 함수를
 * 부르는 이벤트 핸들러 안에서 동기 호출하면 지금 이 순간의 진짜 값을 읽을
 * 수 있다. `getSnapshot` 이 없으면(패널이 이미 언마운트됐거나 아직 마운트
 * 전) 직전에 알려진(대개 이 패널의 마지막 `onSave` cleanup 이 남긴)
 * 카운터로 물러난다 — 여기서 `0` 을 지어내면 안 된다, 과거를 읽던 패널이
 * 전환마다 "안 본 것 없음" 으로 리셋되고 다음 순간 세는 쪽이 옛 `rawItems`
 * 전부를 새로 도착한 것처럼 다시 센다.
 *
 * 앵커·atNewest 보존(P2, codex fix3) — `getSnapshot` 이 이제 앵커/오프셋/핀
 * 상태까지 실어 온다(caption-feed.tsx). 그 값을 **그대로** 쓴다 — DOM 을
 * 다시 재지 않는다. matchMedia 의 change 는 브라우저가 이미 새 폭으로
 * 리플로우를 끝낸 뒤에만 오므로, 그 시점에 DOM 을 재면 줄바꿈이 달라져
 * 있어 같은 스크롤 위치라도 다른 카드가 "맨 위" 로 잡힌다(실측 —
 * 데스크톱→모바일 전환에서 앵커가 한 칸 밀렸다). `getSnapshot` 이 없으면
 * (패널이 이미 언마운트됐거나 스냅샷 계약이 없는 옛 인스턴스) `prev`(마지막
 * `onSave` 가 남긴 값)로 물러나고, 그마저 없으면 마지막 수단으로만 DOM 을
 * 직접 잰다(최초 전환 등 저장된 상태 자체가 없을 때).
 */
/** 테스트 전용 export(codex fix3 item 4) — 아래 주석 참조. */
export function captureLiveState(
  container: HTMLElement | null,
  prev: PanelScrollState | null,
  getSnapshot?:
    | (() => { unseen: number; seenSeq: number } & Pick<
        PanelScrollState,
        'anchorId' | 'anchorOffset' | 'atNewest'
      >)
    | undefined,
): PanelScrollState | null {
  const live = getSnapshot?.()
  if (live) return { ...live }
  if (prev) return { ...prev }
  if (!container) return null

  const feed = container.querySelector<HTMLElement>('[data-testid="caption-feed"]')
  if (!feed) return null
  const unseen = 0
  const seenSeq = 0
  // jump-to-newest 버튼은 caption-feed 스크롤러의 형제다(caption-feed.tsx
  // 의 바깥 wrapper div 아래) — feed 안이 아니라 container 전체에서 찾는다.
  const atNewest = container.querySelector('[data-testid="jump-to-newest"]') === null
  if (atNewest) return { anchorId: null, anchorOffset: 0, atNewest: true, unseen, seenSeq }

  const top = feed.getBoundingClientRect().top
  let anchorId: string | null = null
  let anchorOffset = 0
  for (const node of feed.querySelectorAll<HTMLElement>('[data-item-id]')) {
    const rect = node.getBoundingClientRect()
    if (rect.bottom > top) {
      anchorId = node.dataset['itemId'] ?? null
      anchorOffset = rect.top - top
      break
    }
  }
  return { anchorId, anchorOffset, atNewest: false, unseen, seenSeq }
}

export function SplitFeed({
  liveEmptyTitle,
  liveEmptyDescription,
  onLoadMore,
  ...rest
}: SplitFeedProps): React.ReactElement {
  const shared = useSharedLoadMore(onLoadMore)

  // 패널별 스크롤 상태(D7-5) — SplitFeed 가 소유해 언마운트를 넘어 산다.
  const liveScrollRef = useRef<PanelScrollState | null>(null)
  const longformScrollRef = useRef<PanelScrollState | null>(null)
  const [liveScrollInitial, setLiveScrollInitial] = useState<PanelScrollState | null>(null)
  const [longformScrollInitial, setLongformScrollInitial] = useState<PanelScrollState | null>(
    null,
  )

  // onSave(언마운트 cleanup)는 ref 만 갱신한다 — 그 값은 이 패널이 다음에
  // 새로 마운트될 때 scrollState.initial 로 쓰인다(D7-5). state 로
  // 미러링하지 않는 이유: 전환은 종종 "이 인스턴스 언마운트 + 형제 인스턴스
  // 마운트" 가 같은 커밋에서 겹치고, 새 인스턴스는 scrollState.initial 을
  // 마운트 시 1회만 읽으므로(useState 초기화) onSave 이후의 setState 로는
  // 이미 마운트된 형제에 반영할 길이 없다(실측 — 위치가 매번 최신 끝으로
  // 리셋됐다). 그래서 전환을 **일으키는 이벤트 핸들러 안에서** 떠나는
  // 패널의 현재 위치를 captureLiveState 로 미리 읽어 state 를 굳힌다.
  const saveLive = useCallback((s: PanelScrollState) => {
    liveScrollRef.current = s
  }, [])
  const saveLongform = useCallback((s: PanelScrollState) => {
    longformScrollRef.current = s
  }, [])

  // D7 기준선 보존(codex 지적 #1, fix2) — CaptionFeed 가 매 렌더
  // `scrollState.getSnapshot` 을 직접 대입한다(caption-feed.tsx). 그 대입이
  // 밖으로 보이려면 CaptionFeed 에 **이 ref 의 객체 자체**를 scrollState 로
  // 넘겨야 한다(스프레드로 복제하면 대입이 복제본에만 남는다) — 그래서
  // initial/onSave 도 이 객체의 필드로 유지하고, 아래(liveScrollState 정의
  // 부근)에서 매 렌더 그 필드를 최신 state 로 덮어쓴다.
  type ScrollStateBox = CaptionFeedProps['scrollState'] & object
  const liveSnapshotBoxRef = useRef<ScrollStateBox>({
    initial: null,
    onSave: () => {},
  })
  const longformSnapshotBoxRef = useRef<ScrollStateBox>({
    initial: null,
    onSave: () => {},
  })

  // ≤640px 탭 상태(D5-3) — 기본 실시간, 영속하지 않는다.
  const [mobile, setMobile] = useState(isMobile)
  const [activeTab, setActiveTab] = useState<PanelKey>('live')
  const activeTabRef = useRef(activeTab)
  activeTabRef.current = activeTab
  const [announcement, setAnnouncement] = useState('')

  const liveTabRef = useRef<HTMLButtonElement>(null)
  const longformTabRef = useRef<HTMLButtonElement>(null)
  const liveRegionRef = useRef<HTMLDivElement>(null)
  const longformRegionRef = useRef<HTMLDivElement>(null)
  const livePanelShellRef = useRef<HTMLDivElement>(null)
  const longformPanelShellRef = useRef<HTMLDivElement>(null)

  // 최초 마운트 알림(D6-5).
  const announcedRef = useRef(false)
  useEffect(() => {
    if (announcedRef.current) return
    announcedRef.current = true
    setAnnouncement(announceSplitOn(isMobile()))
  }, [])

  /**
   * D5-3.6·D5-3.7 — 브레이크포인트 전환. 두 방향을 다룬다.
   * 탭→2열: 두 패널이 각자 보존된 상태로 복원된다(스냅샷을 굳혀 둔다).
   * 2열→탭: 포커스가 있던 패널을 선택 탭으로 고른다(포커스 보존, D5-3.7).
   */
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mql = window.matchMedia(BREAKPOINT_QUERY)
    const onChange = (): void => {
      const next = mql.matches
      if (next === mobile) return

      // 전환 직전, 지금 화면에 실제로 마운트돼 있던 패널의 위치를 굳힌다
      // (D5-3.6, 테스트 40). **리사이즈 이후 DOM 을 다시 재서 앵커를
      // 새로 고르지 않는다** — matchMedia 의 change 는 브라우저가 이미 새
      // 폭으로 리플로우를 끝낸 뒤에만 온다. 그 시점에 다시 재면 줄바꿈이
      // 달라져 있어 같은 scrollTop 이라도 다른 카드가 "맨 위" 로 잡힌다
      // (실측 — 데스크톱→모바일 전환에서 앵커가 한 칸 밀렸다). 대신
      // handleScroll 이 스크롤마다 갱신해 둔 ref(D7-5, caption-feed.tsx)를
      // 그대로 쓴다 — 그 값은 **리사이즈 이전** 마지막 스크롤 시점의
      // 정확한 위치다. 그 값이 아직 없으면(스크롤이 한 번도 없었던
      // 최초 상태) DOM 재측정으로 폴백한다.
      if (next) {
        // 데스크톱 → 모바일: 두 패널 모두 마운트돼 있었다(2열). 두 패널 다
        // 여전히 마운트돼 있으므로 getSnapshot 으로 지금 값을 동기 확인한다
        // (codex 지적 #1, fix2 — liveScrollRef.current 가 이미 있어도 그
        // 값은 마지막 onSave 시점의 것일 수 있다).
        const liveState = captureLiveState(
          liveRegionRef.current,
          liveScrollRef.current,
          liveSnapshotBoxRef.current.getSnapshot,
        )
        const longformState = captureLiveState(
          longformRegionRef.current,
          longformScrollRef.current,
          longformSnapshotBoxRef.current.getSnapshot,
        )
        if (liveState) {
          liveScrollRef.current = liveState
          setLiveScrollInitial(liveState)
        }
        if (longformState) {
          longformScrollRef.current = longformState
          setLongformScrollInitial(longformState)
        }

        // 포커스가 담긴 패널을 선택 탭으로 만든다.
        const active = document.activeElement
        const focusedInLongform =
          active != null && longformRegionRef.current?.contains(active) === true
        const focusedInLive = active != null && liveRegionRef.current?.contains(active) === true

        setMobile(true)
        if (focusedInLongform) {
          setActiveTab('longform')
          // 셸은 살지만(선택 탭) 안의 컨트롤 DOM 은 모바일 레이아웃 전환으로
          // 통째로 재마운트된다 — 원래 컨트롤로 되돌릴 길이 없으므로 그
          // 패널의 탭 버튼으로 이관한다.
          requestAnimationFrame(() => longformTabRef.current?.focus())
        } else if (focusedInLive) {
          setActiveTab('live')
          // 위와 같은 이유(desktop↔mobile 은 트리 전체가 재마운트된다) —
          // 실시간 패널 안의 컨트롤도 살아남지 않으므로 그 탭 버튼으로
          // 이관한다(codex 지적 #2 — 이 분기는 원래 포커스 목적지가 없었다).
          requestAnimationFrame(() => liveTabRef.current?.focus())
        } else {
          setActiveTab('live')
        }
      } else {
        // 모바일 → 데스크톱: 선택된 탭의 패널만 마운트돼 있었다 — 그것만 읽는다.
        const nowActiveTab = activeTabRef.current
        const activeRef = nowActiveTab === 'live' ? liveScrollRef : longformScrollRef
        const activeContainer =
          nowActiveTab === 'live' ? livePanelShellRef.current : longformPanelShellRef.current
        const activeSnapshotBox =
          nowActiveTab === 'live' ? liveSnapshotBoxRef.current : longformSnapshotBoxRef.current
        // 선택된 탭만 마운트돼 있다 — 그 패널은 getSnapshot 으로 동기 확인
        // (codex 지적 #1, fix2), 나머지(비마운트) 탭은 기존 ref 값을 쓴다.
        const activeState = captureLiveState(
          activeContainer,
          activeRef.current,
          activeSnapshotBox.getSnapshot,
        )
        if (activeState) {
          if (nowActiveTab === 'live') {
            liveScrollRef.current = activeState
            setLiveScrollInitial(activeState)
          } else {
            longformScrollRef.current = activeState
            setLongformScrollInitial(activeState)
          }
        }

        // 사라진 탭 버튼 또는 그 tabpanel 안(예: 이전 기록 더 보기)에
        // 포커스가 있었으면 대응 region 으로 이관한다(codex 지적 #2 —
        // 원래는 탭 버튼 2개만 인식해 tabpanel 내부 컨트롤의 포커스가
        // 사라졌다).
        const active = document.activeElement
        const wasLiveTab =
          active === liveTabRef.current ||
          (active != null && livePanelShellRef.current?.contains(active) === true)
        const wasLongformTab =
          active === longformTabRef.current ||
          (active != null && longformPanelShellRef.current?.contains(active) === true)
        setMobile(false)
        if (wasLiveTab) {
          requestAnimationFrame(() => liveRegionRef.current?.focus())
        } else if (wasLongformTab) {
          requestAnimationFrame(() => longformRegionRef.current?.focus())
        }
      }
    }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [mobile])

  const commonProps = { onLoadMore: shared.request, ...rest }

  const liveEmptyProps = useMemo(
    () => ({
      ...(liveEmptyTitle !== undefined ? { emptyTitle: liveEmptyTitle } : {}),
      ...(liveEmptyDescription !== undefined ? { emptyDescription: liveEmptyDescription } : {}),
    }),
    [liveEmptyTitle, liveEmptyDescription],
  )

  // liveSnapshotBoxRef.current 를 **그대로**(스프레드로 복제하지 않고) 넘긴다
  // — CaptionFeed 가 매 렌더 이 객체에 getSnapshot 을 직접 대입하므로
  // (caption-feed.tsx), 복제본을 넘기면 그 대입이 이 ref 밖으로 전혀
  // 전달되지 않는다. initial/onSave 는 이 객체의 필드로 매 렌더 갱신한다.
  liveSnapshotBoxRef.current.initial = liveScrollInitial
  liveSnapshotBoxRef.current.onSave = saveLive
  longformSnapshotBoxRef.current.initial = longformScrollInitial
  longformSnapshotBoxRef.current.onSave = saveLongform
  const liveScrollState = liveSnapshotBoxRef.current
  const longformScrollState = longformSnapshotBoxRef.current

  const livePanel = (
    <CaptionFeed
      {...commonProps}
      kinds={LIVE_KINDS}
      live
      scrollState={liveScrollState}
      {...liveEmptyProps}
    />
  )

  const longformPanel = (
    <CaptionFeed
      {...commonProps}
      kinds={LONGFORM_KINDS}
      live={false}
      scrollState={longformScrollState}
      emptyTitle="아직 기록 문단이 없습니다"
      emptyDescription="발화가 쌓이면 최대 60초 창으로 묶여 여기에 정리됩니다."
    />
  )

  const moreButton = shared.request ? (
    <button
      type="button"
      data-testid="longform-load-older"
      className="split-pane-more"
      disabled={shared.exhausted || shared.loading}
      onClick={() => void shared.request?.()}
    >
      {shared.loading ? (
        <Spinner className="h-3 w-3" />
      ) : shared.exhausted ? (
        '더 이전 기록 없음'
      ) : (
        '이전 기록 더 보기'
      )}
    </button>
  ) : null

  // 탭 전환 — 떠나는 탭의 현재 위치를 전환 직전에 DOM 에서 읽어 굳힌다
  // (captureLiveState 주석 참조 — onSave 언마운트 타이밍에 기대지 않는다).
  const selectTab = useCallback(
    (tab: PanelKey) => {
      if (tab === activeTab) return
      const leavingContainer =
        activeTab === 'live' ? livePanelShellRef.current : longformPanelShellRef.current
      const leavingRef = activeTab === 'live' ? liveScrollRef : longformScrollRef
      const leavingSnapshotBox =
        activeTab === 'live' ? liveSnapshotBoxRef.current : longformSnapshotBoxRef.current
      const leavingState = captureLiveState(
        leavingContainer,
        leavingRef.current,
        leavingSnapshotBox.getSnapshot,
      )
      if (leavingState) {
        if (activeTab === 'live') {
          liveScrollRef.current = leavingState
          setLiveScrollInitial(leavingState)
        } else {
          longformScrollRef.current = leavingState
          setLongformScrollInitial(leavingState)
        }
      }
      setActiveTab(tab)
    },
    [activeTab],
  )

  const onTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const order: PanelKey[] = ['live', 'longform']
      const idx = order.indexOf(activeTab)
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault()
        const dir = e.key === 'ArrowRight' ? 1 : -1
        const next = order[(idx + dir + order.length) % order.length] as PanelKey
        selectTab(next)
        ;(next === 'live' ? liveTabRef : longformTabRef).current?.focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        selectTab('live')
        liveTabRef.current?.focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        selectTab('longform')
        longformTabRef.current?.focus()
      }
    },
    [activeTab, selectTab],
  )

  if (mobile) {
    return (
      <div className="split-view flex h-full flex-col">
        <div role="tablist" aria-label="자막 보기" className="split-tabs">
          <button
            ref={liveTabRef}
            type="button"
            role="tab"
            id="split-tab-live"
            aria-selected={activeTab === 'live'}
            aria-controls="split-panel-live"
            tabIndex={activeTab === 'live' ? 0 : -1}
            className="split-tab"
            onClick={() => selectTab('live')}
            onKeyDown={onTabKeyDown}
          >
            실시간
          </button>
          <button
            ref={longformTabRef}
            type="button"
            role="tab"
            id="split-tab-longform"
            aria-selected={activeTab === 'longform'}
            aria-controls="split-panel-longform"
            tabIndex={activeTab === 'longform' ? 0 : -1}
            className="split-tab"
            onClick={() => selectTab('longform')}
            onKeyDown={onTabKeyDown}
          >
            긴호흡
          </button>
        </div>

        <div
          ref={livePanelShellRef}
          id="split-panel-live"
          role="tabpanel"
          aria-labelledby="split-tab-live"
          hidden={activeTab !== 'live'}
          tabIndex={activeTab === 'live' ? 0 : -1}
          className="split-pane-body min-h-0 flex-1"
        >
          {activeTab === 'live' && livePanel}
        </div>
        <div
          ref={longformPanelShellRef}
          id="split-panel-longform"
          role="tabpanel"
          aria-labelledby="split-tab-longform"
          hidden={activeTab !== 'longform'}
          tabIndex={activeTab === 'longform' ? 0 : -1}
          className="split-pane-body min-h-0 flex-1"
        >
          {activeTab === 'longform' && (
            <div className="flex h-full flex-col">
              <div className="split-pane-head">
                긴호흡 기록
                <span className="split-pane-hint">창이 닫히면(최대 60초) 문단이 도착합니다</span>
                {moreButton}
              </div>
              <div className="split-pane-body min-h-0 flex-1">{longformPanel}</div>
            </div>
          )}
        </div>

        <div aria-live="polite" className="sr-only">
          {announcement}
        </div>
      </div>
    )
  }

  return (
    <div className="split-view">
      <div ref={liveRegionRef} role="region" aria-label="실시간 통역" tabIndex={-1} className="split-pane">
        <div className="split-pane-head">실시간</div>
        <div className="split-pane-body">{livePanel}</div>
      </div>
      <div
        ref={longformRegionRef}
        role="region"
        aria-label="긴호흡 기록"
        tabIndex={-1}
        className="split-pane split-pane-longform"
      >
        <div className="split-pane-head">
          긴호흡 기록
          <span className="split-pane-hint">창이 닫히면(최대 60초) 문단이 도착합니다</span>
          {moreButton}
        </div>
        <div className="split-pane-body">{longformPanel}</div>
      </div>

      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  )
}
